import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Row, Col, Table, Button, Spinner, Alert, Modal, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { adminApi, extractError } from '../api/client';
import { fmtRelative, fmtDate } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';
import { useCounties } from '../hooks/useLocations';
import { SOURCE_TYPES, PARSING_METHODS, DS_STATUSES, sourceTypeLabel, parsingMethodLabel, dsStatusMeta } from '../lib/dataSourceMeta';

export default function DataSourcesListPage() {
  const { stateId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [state, setState] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [q, setQ] = useState('');
  const [sourceType, setSourceType] = useState(params.get('source_type') || '');
  const [parsingMethod, setParsingMethod] = useState('');
  const [status, setStatus] = useState(params.get('status') || '');
  const [countyFilter, setCountyFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { counties } = useCounties(stateId);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    adminApi.get(`/admin/states/${stateId}`).then((r) => setState(r.data)).catch(() => {});
  }, [stateId]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const p = { page, page_size: pageSize };
      if (q.trim()) p.q = q.trim();
      if (sourceType) p.source_type = sourceType;
      if (parsingMethod) p.parsing_method = parsingMethod;
      if (status) p.status = status;
      if (countyFilter) p.county_id = countyFilter;
      const res = await adminApi.get(`/admin/states/${stateId}/data-sources`, { params: p });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId, page, q, sourceType, parsingMethod, status, countyFilter]);

  // Sync URL state when filters change so deep-links are shareable
  useEffect(() => {
    const next = new URLSearchParams();
    if (sourceType) next.set('source_type', sourceType);
    if (status) next.set('status', status);
    setParams(next, { replace: true });
  }, [sourceType, status, setParams]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/states/${stateId}/data-sources/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  }

  function countyName(id) {
    if (!id) return '—';
    return counties.find((c) => c.id === id)?.name || id.slice(0, 8) + '…';
  }

  return (
    <>
      <PageHeader
        title="Data Sources"
        subtitle={state ? `${total} source${total === 1 ? '' : 's'} · ${state.name} (${state.abbreviation})` : `${total} sources`}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}{state && <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link>}
            {' / '}<Link to={`/admin/states/${stateId}/data-management`} className="text-decoration-none">Data Management</Link>
            {' / '}Sources
          </span>
        }
        actions={
          <Button variant="primary" onClick={() => navigate(`/admin/states/${stateId}/data-sources/new`)} data-testid="data-sources-create-button">
            <i className="bi bi-plus-lg me-1" /> New data source
          </Button>
        }
      />
      <div className="page-content">
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col xs={12} md={4}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control placeholder="Name, URL, notes…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} data-testid="data-sources-search-input" />
                </InputGroup>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Source type</Form.Label>
                <Form.Select value={sourceType} onChange={(e) => { setPage(1); setSourceType(e.target.value); }} data-testid="data-sources-type-select">
                  <option value="">All</option>
                  {SOURCE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Method</Form.Label>
                <Form.Select value={parsingMethod} onChange={(e) => { setPage(1); setParsingMethod(e.target.value); }} data-testid="data-sources-method-select">
                  <option value="">All</option>
                  {PARSING_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="data-sources-status-select">
                  <option value="">All</option>
                  {DS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="mb-1" style={{ fontSize: '0.82rem' }}>County</Form.Label>
                <Form.Select value={countyFilter} onChange={(e) => { setPage(1); setCountyFilter(e.target.value); }} data-testid="data-sources-county-select">
                  <option value="">Any (incl. state-level)</option>
                  {counties.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-link-45deg" />
              <div>No data sources yet.</div>
              <Button variant="primary" className="mt-2" onClick={() => navigate(`/admin/states/${stateId}/data-sources/new`)}>Add your first source</Button>
            </div>
          ) : (
            <>
              <Table hover responsive size="sm" className="mb-0" data-testid="data-sources-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>County</th>
                    <th>Method</th>
                    <th>Frequency</th>
                    <th>Status</th>
                    <th>Last imported</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const meta = dsStatusMeta(s.status);
                    return (
                      <tr key={s.id} data-testid={`data-source-row-${s.id}`}>
                        <td>
                          <Link to={`/admin/states/${stateId}/data-sources/${s.id}`} className="text-decoration-none fw-semibold">{s.name}</Link>
                          <div className="text-muted" style={{ fontSize: '0.78rem', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <a href={s.source_url} target="_blank" rel="noreferrer">{s.source_url}</a>
                          </div>
                        </td>
                        <td>{sourceTypeLabel(s.source_type)}</td>
                        <td>{countyName(s.county_id)}</td>
                        <td>{parsingMethodLabel(s.parsing_method)}</td>
                        <td className="text-muted">{s.import_frequency || '—'}</td>
                        <td><span className={`status-badge ${meta.badge}`}>{meta.label}</span></td>
                        <td className="text-muted">{s.last_imported_at ? fmtRelative(s.last_imported_at) : <span className="text-muted">never</span>}</td>
                        <td className="text-muted">{fmtRelative(s.updated_at)}</td>
                        <td className="text-end">
                          <div className="d-flex gap-1 justify-content-end">
                            <Link to={`/admin/states/${stateId}/data-sources/${s.id}`}><Button size="sm" variant="outline-primary" data-testid={`data-source-edit-${s.id}`}>Edit</Button></Link>
                            <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(s)} data-testid={`data-source-delete-${s.id}`}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Page {page} of {totalPages} · {total} sources</div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton><Modal.Title>Delete data source</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-data-source-button">
            {deleting ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
